import is from '@sindresorhus/is';
import { type FunctionComponent, useCallback, useEffect, useRef } from 'react';

import { MAIN_TILE_ID, TILE_WIDTH_CSS_TRANSITION } from './constants';
import {
    useMainWidthLockstep,
    useSetTileBox,
    useTileErrorBoundary,
    useTileWidthContext,
    useTilingEngineContext,
} from './TilingEngineContext';
import type { TilingEngineMainProps } from './types';

export const TilingEngineMain: FunctionComponent<TilingEngineMainProps> = ({ children, minWidth }) => {
    const { notify } = useTilingEngineContext();
    const setTileBox = useSetTileBox();
    const tileWidths = useTileWidthContext();
    const isWidthLockstep = useMainWidthLockstep();
    const ErrorBoundary = useTileErrorBoundary();

    const widthStyle = tileWidths.get(MAIN_TILE_ID);
    const width = widthStyle?.width ?? '100%';

    const previousWidthRef = useRef<string | null>(null);

    const setOuterRef = useCallback(
        (node: HTMLDivElement | null) => {
            setTileBox(MAIN_TILE_ID, node);
        },
        [setTileBox],
    );

    useEffect(() => {
        if (previousWidthRef.current === null) {
            previousWidthRef.current = width;
            return;
        }

        if (previousWidthRef.current === width) {
            return;
        }

        previousWidthRef.current = width;
        notify({ type: 'tileOpenStart', tileId: MAIN_TILE_ID });
        notify({ type: 'tileOpenComplete', tileId: MAIN_TILE_ID });
    }, [width, notify]);

    return (
        <div
            ref={setOuterRef}
            data-tile-id={MAIN_TILE_ID}
            style={{
                flexShrink: 0,
                height: '100%',
                maxWidth: widthStyle?.maxWidth,
                minWidth: widthStyle?.minWidth ?? minWidth,
                transition: isWidthLockstep ? TILE_WIDTH_CSS_TRANSITION : 'none',
                width,
            }}
        >
            <div className="h-full w-full flex flex-col">
                {is.nullOrUndefined(ErrorBoundary) ? (
                    children
                ) : (
                    <ErrorBoundary tileId={MAIN_TILE_ID}>{children}</ErrorBoundary>
                )}
            </div>
        </div>
    );
};
